import React from 'react'
import { BookOpenCheck, Edit2, ExternalLink, Settings2 } from 'lucide-react'
import { WorkspaceProjectState, WorkspaceState } from '../../lib/workspace'
import LifecycleStepper from './LifecycleStepper'

interface WorkspaceHeaderProps {
  title: string
  project?: WorkspaceProjectState | null
  state: WorkspaceState
  lastUpdated?: string | null
  onRename: () => void
  onOpenSettings: () => void
  briefingAvailable?: boolean
  briefingOpen?: boolean
  onToggleBriefing?: () => void
}

function formatUpdated(value?: string | null) {
  if (!value) return 'No activity yet'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Update time unavailable'
  return `Updated ${new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date)}`
}

export default function WorkspaceHeader({
  title, project, state, lastUpdated, onRename, onOpenSettings,
  briefingAvailable, briefingOpen, onToggleBriefing,
}: WorkspaceHeaderProps) {
  return (
    <header className="workspace-header">
      <div className="workspace-header-main">
        <div className="workspace-identity">
          <div className="workspace-eyebrow">
            <span>{project?.name || 'Connector project'}</span>
            <span aria-hidden="true">/</span>
            <span>{project?.connection_name || 'Connection not set'}</span>
          </div>
          <div className="workspace-title-row">
            <h1>{title || 'New conversation'}</h1>
            {state.connectorFamily !== 'Unknown' && <span className="workspace-connector-badge">{state.connectorFamily}</span>}
            <span className={`workspace-status-badge ${state.status}`}>{state.statusLabel}</span>
          </div>
          <div className="workspace-meta">
            <span>{formatUpdated(lastUpdated)}</span>
            <span>{project?.deployment_mode === 'terraform' ? 'Terraform deployment' : 'Direct deployment'}</span>
            {state.statusSource === 'inferred' && <span>State inferred</span>}
          </div>
        </div>

        <div className="workspace-header-actions">
          <button type="button" onClick={onRename}><Edit2 size={14} /> Rename</button>
          <button type="button" onClick={onOpenSettings}><Settings2 size={14} /> Settings</button>
          {briefingAvailable && onToggleBriefing && (
            <button type="button" onClick={onToggleBriefing} aria-pressed={briefingOpen}>
              <BookOpenCheck size={14} /> {briefingOpen ? 'Hide briefing' : 'Briefing'}
            </button>
          )}
          <button type="button" disabled title="Export packages arrive in Agent v2 Phase 3">
            <ExternalLink size={14} /> Export
          </button>
        </div>
      </div>
      <LifecycleStepper phases={state.phases} />
    </header>
  )
}
